import React, { useState, useEffect, useRef } from 'react';
import { getCurrentLocation, reverseGeocode } from '../../utils/geocoding';

interface GPSLocationPickerProps {
  onLocationSelect: (address: string, lat: number, lng: number) => void;
  initialAddress?: string;
  initialLat?: number;
  initialLng?: number;
  placeholder?: string;
  label?: string;
  required?: boolean;
  className?: string;
}

const GPSLocationPicker: React.FC<GPSLocationPickerProps> = ({
  onLocationSelect,
  initialAddress = '',
  initialLat,
  initialLng,
  placeholder = 'Enter pickup location...',
  label = 'Pickup Location',
  required = false,
  className = ''
}) => {
  const [address, setAddress] = useState(initialAddress);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapLocation, setMapLocation] = useState<{ lat: number; lng: number } | null>(
    initialLat && initialLng ? { lat: initialLat, lng: initialLng } : null
  );
  const mapRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<any>(null);

  // Load Google Maps if showing map
  useEffect(() => {
    if (!showMap || !mapRef.current) return;

    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY'}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeMap;
      document.head.appendChild(script);
    } else {
      initializeMap();
    }
  }, [showMap]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;

    const center = mapLocation || { lat: -24.6282, lng: 25.9231 }; // Gaborone, Botswana

    const mapOptions = {
      center,
      zoom: mapLocation ? 15 : 12,
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }]
        }
      ],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true
    };

    const map = new window.google.maps.Map(mapRef.current, mapOptions);

    // Add marker if we have a location
    if (mapLocation) {
      markerRef.current = new window.google.maps.Marker({
        position: mapLocation,
        map,
        draggable: true,
        title: 'Pickup Location'
      });

      // Handle marker drag
      markerRef.current.addListener('dragend', async (event: any) => {
        const newPos = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        };
        setMapLocation(newPos);

        // Reverse geocode to get address
        try {
          const newAddress = await reverseGeocode(newPos.lat, newPos.lng);
          if (newAddress) {
            setAddress(newAddress);
            onLocationSelect(newAddress, newPos.lat, newPos.lng);
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
        }
      });
    }

    // Handle map clicks
    map.addListener('click', async (event: any) => {
      const clickedPos = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };

      setMapLocation(clickedPos);

      // Update or create marker
      if (markerRef.current) {
        markerRef.current.setPosition(clickedPos);
      } else {
        markerRef.current = new window.google.maps.Marker({
          position: clickedPos,
          map,
          draggable: true,
          title: 'Pickup Location'
        });

        markerRef.current.addListener('dragend', async (event: any) => {
          const newPos = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
          };
          setMapLocation(newPos);

          try {
            const newAddress = await reverseGeocode(newPos.lat, newPos.lng);
            if (newAddress) {
              setAddress(newAddress);
              onLocationSelect(newAddress, newPos.lat, newPos.lng);
            }
          } catch (error) {
            console.error('Error reverse geocoding:', error);
          }
        });
      }

      // Reverse geocode to get address
      try {
        const newAddress = await reverseGeocode(clickedPos.lat, clickedPos.lng);
        if (newAddress) {
          setAddress(newAddress);
          onLocationSelect(newAddress, clickedPos.lat, clickedPos.lng);
        }
      } catch (error) {
        console.error('Error reverse geocoding:', error);
      }
    });
  };

  const handleUseGPSLocation = async () => {
    setIsGettingLocation(true);
    try {
      const location = await getCurrentLocation();
      setMapLocation(location);

      // Reverse geocode to get address
      const addressFromCoords = await reverseGeocode(location.lat, location.lng);
      if (addressFromCoords) {
        setAddress(addressFromCoords);
        onLocationSelect(addressFromCoords, location.lat, location.lng);
      } else {
        // Fallback to coordinates if reverse geocoding fails
        const coordAddress = `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`;
        setAddress(coordAddress);
        onLocationSelect(coordAddress, location.lat, location.lng);
      }
    } catch (error: any) {
      console.error('Error getting GPS location:', error);
      alert(`Failed to get your location: ${error.message || 'Unknown error'}`);
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
  };

  const handleAddressBlur = () => {
    // If address changed and we have coordinates, update the callback
    if (address && mapLocation) {
      onLocationSelect(address, mapLocation.lat, mapLocation.lng);
    }
  };

  const toggleMap = () => {
    setShowMap(!showMap);
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Address Input */}
      <div className="flex space-x-2 mb-3">
        <div className="flex-1 relative">
          <input
            type="text"
            value={address}
            onChange={handleAddressChange}
            onBlur={handleAddressBlur}
            placeholder={placeholder}
            required={required}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-10"
          />
          {address && (
            <button
              type="button"
              onClick={() => {
                setAddress('');
                setMapLocation(null);
                onLocationSelect('', 0, 0);
              }}
              className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={handleUseGPSLocation}
          disabled={isGettingLocation}
          className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isGettingLocation ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Getting...
            </div>
          ) : (
            <>
              📍 GPS
            </>
          )}
        </button>
      </div>

      {/* Map Toggle */}
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-gray-600">
          {mapLocation ? (
            <span className="text-green-600">
              ✓ Location set: {mapLocation.lat.toFixed(4)}, {mapLocation.lng.toFixed(4)}
            </span>
          ) : (
            'No location selected'
          )}
        </div>

        <button
          type="button"
          onClick={toggleMap}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
        >
          {showMap ? '🗺️ Hide Map' : '🗺️ Select on Map'}
        </button>
      </div>

      {/* Map Container */}
      {showMap && (
        <div className="border border-gray-300 rounded-lg overflow-hidden mb-3">
          <div ref={mapRef} className="h-64 w-full" />
          <div className="p-3 bg-gray-50 text-sm text-gray-600">
            <strong>How to use:</strong> Click on the map to set pickup location, or drag the marker to adjust position.
          </div>
        </div>
      )}

      {/* Location Tips */}
      <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
        <strong>Tips:</strong> Use GPS for accurate location, or enter address manually. For best results, use the map to fine-tune your exact pickup spot.
      </div>
    </div>
  );
};

export default GPSLocationPicker;
